import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  FormControl,
  InputLabel,
  Select,
  Grid,
  CircularProgress,
  Alert,
  Pagination,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Timeline as TimelineIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../components/Layout/MainLayout';
import { useAdminCompanies } from '../../hooks/useAdminManagement';
import {
  CompanySearchFilters,
  CompanyApprovalRequest,
} from '../../types/admin-management';
import { adminManagementService } from '../../services/adminManagementService';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

const AdminCompanyManagementPage: React.FC = () => {
  const { companies, isLoading, error, fetchCompanies, updateCompanyApproval } =
    useAdminCompanies();
  const [filters, setFilters] = useState<CompanySearchFilters>({});
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [companyDetailDialogOpen, setCompanyDetailDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalCompanyId, setApprovalCompanyId] = useState<number | null>(
    null
  );
  const [approvalData, setApprovalData] = useState<CompanyApprovalRequest>({
    action: 'approve',
    reason: '',
    notify_company: true,
  });
  const [companyDetailLoading, setCompanyDetailLoading] = useState(false);

  const handleSearch = () => {
    fetchCompanies(filters);
  };

  const handleReset = () => {
    setFilters({});
    fetchCompanies({});
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    fetchCompanies({ ...filters, page: value });
  };

  const openCompanyDetail = async (company: any) => {
    try {
      setCompanyDetailLoading(true);
      setCompanyDetailDialogOpen(true);

      // 企業詳細APIを呼び出して完全なデータを取得
      const detailedCompany = await adminManagementService.getCompanyDetail(
        company.id
      );
      setSelectedCompany(detailedCompany);
    } catch (error) {
      console.error('企業詳細の取得に失敗しました:', error);
      // エラーの場合は基本情報のみでモーダルを開く
      setSelectedCompany(company);
    } finally {
      setCompanyDetailLoading(false);
    }
  };

  const openApprovalDialog = (companyId: number, currentStatus: string) => {
    setApprovalCompanyId(companyId);
    setApprovalData({
      action: currentStatus === 'pending' ? 'approve' : 'reject',
      reason: '',
      notify_company: true,
    });
    setApprovalDialogOpen(true);
  };

  const handleApproval = async () => {
    if (!approvalCompanyId) return;

    const result = await updateCompanyApproval(approvalCompanyId, approvalData);
    if (result.success) {
      setApprovalDialogOpen(false);
      setApprovalCompanyId(null);
      setApprovalData({ action: 'approve', reason: '', notify_company: true });
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'approved':
        return <Chip label="承認済み" color="success" size="small" />;
      case 'pending':
        return <Chip label="承認待ち" color="warning" size="small" />;
      case 'rejected':
        return <Chip label="拒否" color="error" size="small" />;
      case 'suspended':
        return <Chip label="停止中" color="error" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  if (isLoading && !companies) {
    return (
      <MainLayout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            企業管理
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            登録企業の管理と承認処理
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* 検索フィルター */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            検索・フィルター
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="企業名"
                value={filters.company_name || ''}
                onChange={(e) =>
                  setFilters({ ...filters, company_name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="業種"
                value={filters.business_type || ''}
                onChange={(e) =>
                  setFilters({ ...filters, business_type: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="都道府県"
                value={filters.prefecture || ''}
                onChange={(e) =>
                  setFilters({ ...filters, prefecture: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>ステータス</InputLabel>
                <Select
                  value={filters.status || ''}
                  label="ステータス"
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value })
                  }
                >
                  <MenuItem value="">すべて</MenuItem>
                  <MenuItem value="pending">承認待ち</MenuItem>
                  <MenuItem value="approved">承認済み</MenuItem>
                  <MenuItem value="rejected">拒否</MenuItem>
                  <MenuItem value="suspended">停止中</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={isLoading}
            >
              検索
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              disabled={isLoading}
            >
              リセット
            </Button>
          </Box>
        </Paper>

        {/* 企業一覧テーブル */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>企業名</TableCell>
                  <TableCell>代表者</TableCell>
                  <TableCell>業種</TableCell>
                  <TableCell>所在地</TableCell>
                  <TableCell>従業員数</TableCell>
                  <TableCell>登録日</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies?.companies.map((company) => {
                  const isDeleted = company.deleted_at && company.deleted_at !== null && company.deleted_at !== '';
                  
                  return (
                    <TableRow 
                      key={company.id} 
                      hover={!isDeleted}
                      sx={{
                        backgroundColor: isDeleted ? '#f5f5f5' : 'inherit',
                        opacity: isDeleted ? 0.75 : 1,
                        '&:hover': {
                          backgroundColor: isDeleted ? '#eeeeee' : undefined
                        }
                      }}
                    >
                      <TableCell>{company.id}</TableCell>
                      <TableCell>
                        {company.company_name}
                        {isDeleted && (
                          <Box component="span" sx={{ ml: 0.5, color: 'error.main', fontWeight: 'bold', fontSize: '0.7rem' }}>
                            (退会)
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: isDeleted ? 'text.secondary' : 'inherit' }}>
                        {company.representative_name}
                      </TableCell>
                      <TableCell sx={{ color: isDeleted ? 'text.secondary' : 'inherit' }}>
                        {company.business_type}
                      </TableCell>
                      <TableCell sx={{ color: isDeleted ? 'text.secondary' : 'inherit' }}>
                        {company.prefecture} {company.city}
                      </TableCell>
                      <TableCell sx={{ color: isDeleted ? 'text.secondary' : 'inherit' }}>
                        {company.employee_count_range}
                      </TableCell>
                      <TableCell sx={{ color: isDeleted ? 'text.secondary' : 'inherit' }}>
                        {format(
                          new Date(company.registration_date),
                          'yyyy/MM/dd',
                          { locale: ja }
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {isDeleted ? (
                            <Chip
                              label="退会済み"
                              color="error"
                              variant="filled"
                              size="small"
                              sx={{
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                backgroundColor: '#d32f2f',
                                color: 'white'
                              }}
                            />
                          ) : (
                            getStatusChip(company.status)
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="詳細表示">
                            <IconButton
                              size="small"
                              onClick={() => openCompanyDetail(company)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          {!isDeleted && company.status === 'pending' && (
                            <>
                              <Tooltip title="承認">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    openApprovalDialog(company.id, company.status)
                                  }
                                  color="success"
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="拒否">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setApprovalCompanyId(company.id);
                                    setApprovalData({
                                      action: 'reject',
                                      reason: '',
                                      notify_company: true,
                                    });
                                    setApprovalDialogOpen(true);
                                  }}
                                  color="error"
                                >
                                  <CancelIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ページネーション */}
          {companies && companies.pagination.total_pages > 1 && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={companies.pagination.total_pages}
                page={companies.pagination.current_page}
                onChange={handlePageChange}
                disabled={isLoading}
              />
            </Box>
          )}
        </Paper>

        {/* 企業詳細ダイアログ */}
        <Dialog
          open={companyDetailDialogOpen}
          onClose={() => setCompanyDetailDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              企業詳細
              {selectedCompany?.deleted_at && (
                <Chip
                  label="退会済み"
                  color="error"
                  variant="filled"
                  size="small"
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    backgroundColor: '#d32f2f',
                    color: 'white'
                  }}
                />
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedCompany && (
              <Box>
                {/* 退会日時の警告表示 */}
                {selectedCompany.deleted_at && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    この企業は退会済みです（退会日時: {format(new Date(selectedCompany.deleted_at), 'yyyy年MM月dd日 HH:mm', { locale: ja })}）
                  </Alert>
                )}
                
                {/* 基本情報 */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      基本情報
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          企業名
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {selectedCompany.company_name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          代表者
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {selectedCompany.representative_name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          業種
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {selectedCompany.business_type}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          従業員数
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {selectedCompany.employee_count_range}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          所在地
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {selectedCompany.prefecture} {selectedCompany.city}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          連絡先
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {selectedCompany.contact_email}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* プラットフォーム活動 */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      プラットフォーム活動
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={6} sm={3}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: '50%',
                              backgroundColor: 'primary.100',
                              color: 'primary.600',
                              mb: 1,
                            }}
                          >
                            <PeopleIcon />
                          </Box>
                          <Typography variant="h6">
                            {selectedCompany.platform_activity
                              ?.total_employees ||
                              selectedCompany.employee_count ||
                              0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            従業員数
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: '50%',
                              backgroundColor: 'success.100',
                              color: 'success.600',
                              mb: 1,
                            }}
                          >
                            <ChatIcon />
                          </Box>
                          <Typography variant="h6">
                            {selectedCompany.platform_activity
                              ?.scout_messages_sent ||
                              selectedCompany.scout_messages_sent ||
                              0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            スカウト送信数
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: '50%',
                              backgroundColor: 'info.100',
                              color: 'info.600',
                              mb: 1,
                            }}
                          >
                            <TimelineIcon />
                          </Box>
                          <Typography variant="h6">
                            {selectedCompany.platform_activity
                              ?.timeline_posts_created || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            投稿数
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* 従業員一覧 */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    従業員一覧
                  </Typography>
                  {companyDetailLoading ? (
                    <Box
                      sx={{ display: 'flex', justifyContent: 'center', p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : selectedCompany.employees &&
                    selectedCompany.employees.length > 0 ? (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>名前</TableCell>
                            <TableCell>メールアドレス</TableCell>
                            <TableCell>役職</TableCell>
                            <TableCell>最終ログイン</TableCell>
                            <TableCell>ステータス</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedCompany.employees?.length > 0 ? (
                            selectedCompany.employees.map((employee: any) => {
                              const isDeleted = employee.user_status === 'DELETED';
                              
                              return (
                                <TableRow 
                                  key={employee.user_id}
                                  sx={{
                                    backgroundColor: isDeleted ? '#f5f5f5' : 'inherit',
                                    opacity: isDeleted ? 0.75 : 1,
                                    '&:hover': {
                                      backgroundColor: isDeleted ? '#eeeeee' : undefined
                                    }
                                  }}
                                >
                                  <TableCell sx={{ color: isDeleted ? 'text.secondary' : 'inherit' }}>
                                    {employee.full_name}
                                    {isDeleted && (
                                      <Box component="span" sx={{ ml: 0.5, color: 'error.main', fontWeight: 'bold', fontSize: '0.7rem' }}>
                                        (退会)
                                      </Box>
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ color: isDeleted ? 'text.secondary' : 'inherit' }}>
                                    {employee.email}
                                  </TableCell>
                                  <TableCell sx={{ color: isDeleted ? 'text.secondary' : 'inherit' }}>
                                    {employee.role}
                                  </TableCell>
                                  <TableCell sx={{ color: isDeleted ? 'text.secondary' : 'inherit' }}>
                                    {employee.last_login_at
                                      ? format(
                                          new Date(employee.last_login_at),
                                          'yyyy/MM/dd HH:mm',
                                          { locale: ja }
                                        )
                                      : '-'}
                                  </TableCell>
                                  <TableCell>
                                    <Stack direction="row" spacing={1}>
                                      {/* 退会ユーザーの場合は退会済みを優先表示 */}
                                      {isDeleted ? (
                                        <Chip
                                          label="退会済み"
                                          color="error"
                                          variant="filled"
                                          size="small"
                                          sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: '0.75rem',
                                            backgroundColor: '#d32f2f',
                                            color: 'white'
                                          }}
                                        />
                                      ) : (
                                        <Chip
                                          label={
                                            employee.is_active
                                              ? 'アクティブ'
                                              : '停止中'
                                          }
                                          color={
                                            employee.is_active ? 'success' : 'error'
                                          }
                                          size="small"
                                        />
                                      )}
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          ) : (
                            <TableRow>
                              <TableCell colSpan={5} align="center">
                                従業員データがありません
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ p: 2 }}
                    >
                      従業員データがありません
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCompanyDetailDialogOpen(false)}>
              閉じる
            </Button>
          </DialogActions>
        </Dialog>

        {/* 承認・拒否ダイアログ */}
        <Dialog
          open={approvalDialogOpen}
          onClose={() => setApprovalDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            企業{approvalData.action === 'approve' ? '承認' : '拒否'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>アクション</InputLabel>
                  <Select
                    value={approvalData.action}
                    label="アクション"
                    onChange={(e) =>
                      setApprovalData({
                        ...approvalData,
                        action: e.target.value as any,
                      })
                    }
                  >
                    <MenuItem value="approve">承認</MenuItem>
                    <MenuItem value="reject">拒否</MenuItem>
                    <MenuItem value="request_more_info">追加情報要求</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="理由・コメント"
                  multiline
                  rows={3}
                  value={approvalData.reason || ''}
                  onChange={(e) =>
                    setApprovalData({
                      ...approvalData,
                      reason: e.target.value,
                    })
                  }
                  required
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApprovalDialogOpen(false)}>
              キャンセル
            </Button>
            <Button
              onClick={handleApproval}
              variant="contained"
              color={approvalData.action === 'approve' ? 'success' : 'error'}
              disabled={!approvalData.reason?.trim()}
            >
              {approvalData.action === 'approve' ? '承認' : '拒否'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </MainLayout>
  );
};

export default AdminCompanyManagementPage;
